import { useState, useEffect } from 'react';
import { CalendarCheck } from 'lucide-react';
import ScheduleForm from '../../components/ScheduleForm/ScheduleForm';
import { useAuthValue } from '../../context/AuthContext';
import { useInsertDocument } from '../../hooks/useInsertDocument';
import { useFetchDocuments } from '../../hooks/useFetchDocuments';
import { useFetchEnabledHours } from '../../hooks/useFetchEnabledHours';
import { useHours } from '../../hooks/useHours';
import { useAdminValue } from '../../context/AdminContext';
import { useFetchDayOff } from '../../hooks/useFetchDayOff';
import { useToast } from '../../components/ui';
import {
  getAppointmentHoursForService,
  getAvailableStartHours,
  getOccupiedAppointmentHours,
  getTerminalBlockedHours,
  hasValidAppointmentHours,
} from '../../utilities/appointmentSlots';
import {
  buildLocalDateTime,
  formatLocalDateDisplay,
  formatLocalDateInput,
  parseLocalDateInput,
} from '../../utilities/dateUtils';
import { Card } from '../../components/ui';

const uuid = require('uuid');

const barbers = ['Carlos', 'Donizete'];

export default function Home() {
  const [name, setName] = useState('');
  const [cel, setCel] = useState('');
  const [date, setDate] = useState('');
  const [hour, setHour] = useState([]);
  const [service, setService] = useState('');
  const [professional, setProfessional] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [appointmentHours, setAppointmentHours] = useState([]);
  const [hoursIndex, setIndex] = useState();
  const [noDayOffBarber, setNoDayOffBarber] = useState(barbers);
  const [exclusiveHours, setExclusiveHours] = useState([]);

  const { isAdmin } = useAdminValue();
  const { hoursObject } = useHours(date);
  const toast = useToast();

  const handleRedirect = (hour, professional, service, tid) => {
    window.location.href = `https://wa.me/19999787030?text=Olá, sou o ${name.toUpperCase()} e confirmo meu agendamento com os seguintes dados:%0A%0AData: *${formatLocalDateDisplay(date)}*%0AHorário: *${hour}*%0ABarbeiro: *${professional}*%0AServiço: *${service}*%0AID do agendamento: *${tid}*%0ACaso seja necessário cancelar seu agendamento, utilize o link abaixo: %0Ahttps://salaolima.com/cancel-appointment/${tid}%0A%0A_ENVIE ESSA MENSAGEM PARA CONFIRMAR SEU AGENDAMENTO_`;
  };

  const { user: authUser } = useAuthValue();
  const { setDocument } = useInsertDocument('transactions');
  const { documents, loading: loadingUserData, error } = useFetchDocuments('users', authUser ? authUser.uid : null);
  const { documents: enabledHours } = useFetchEnabledHours('transactions', professional, date);
  const { documents: dayOffs } = useFetchDayOff('day-off', date);

  const uid = authUser ? authUser.uid : null;

  const handleDateChange = (e) => {
    const chosenDate = parseLocalDateInput(e.target.value);

    if (!chosenDate) {
      setDate('');
      setHour([]);
      setIndex(undefined);
      setService('');
      setProfessional('');
      return;
    }

    const chosenDateInput = formatLocalDateInput(chosenDate);

    if (chosenDate.getDay() === 0) {
      toast.error('Não atendemos aos domingos. Escolha outra data.');
      setDate('');
    } else {
      setDate(chosenDateInput);
    }

    setHour([]);
    setIndex(undefined);
  };

  useEffect(() => {
    const dayOffBarbers = Array.from(new Set(
      (dayOffs || []).map((d) => d.professional).filter(Boolean),
    ));

    if (dayOffBarbers.length > 0) {
      const working = barbers.filter((b) => !dayOffBarbers.includes(b));
      setNoDayOffBarber(working);
      // Sem aviso: barbeiro de folga ja aparece desabilitado no card.
      // Clear silencioso como rede de seguranca caso a data mude apos a selecao.
      if (professional && dayOffBarbers.includes(professional)) {
        setProfessional('');
        setHour([]);
        setIndex(undefined);
      }
    } else {
      setNoDayOffBarber(barbers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOffs]);

  const handleHourChange = (e) => {
    const selectedHour = e.target.value;

    if (!selectedHour) { setHour([]); setIndex(undefined); return; }

    if (!service) {
      setHour([]); setIndex(undefined);
      toast.error('Selecione um serviço antes do horário.');
      return;
    }

    const hours = Array.isArray(hoursObject) ? hoursObject.map((h) => h.hour) : [];
    const selectedHours = getAppointmentHoursForService({ startHour: selectedHour, service, availableHours: hours });

    if (!selectedHours.length || selectedHours.some((h) => appointmentHours.includes(h))) {
      setHour([]); setIndex(undefined);
      toast.error('Este horário não comporta o serviço. Escolha outro.');
      return;
    }

    setHour(selectedHours);
    setIndex(hours.indexOf(selectedHour) + 1);
  };

  const handleCelChange = (e) => {
    const raw = e.target.value.replace(/\(|\)|-| /g, '');
    if (raw.match(/[a-zA-Z]/)) return;
    if (raw.length >= 12) return;
    setCel(e.target.value);
    if (e.target.value.length === 11) {
      const arr = raw.split('');
      arr.splice(0, 0, '('); arr.splice(3, 0, ')'); arr.splice(4, 0, ' '); arr.splice(10, 0, '-');
      setCel(arr.join(''));
    }
  };

  const handleProfessionalChange = (e) => {
    setHour([]); setIndex(undefined);
    setProfessional(e.target.value);
  };

  const handelServiceChange = (e) => {
    setProfessional(''); setHour([]); setIndex(undefined);
    setService(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!navigator.onLine) {
      toast.error('Sem internet. Verifique sua conexão e tente novamente.');
      return;
    }

    const availableHours = Array.isArray(hoursObject) ? hoursObject.map((h) => h.hour) : [];
    const validStartHours = getAvailableStartHours({ service, availableHours, occupiedHours: appointmentHours });

    if (!hasValidAppointmentHours(hour) || !validStartHours.includes(hour[0])) {
      toast.error('Selecione um horário válido.');
      return;
    }

    const tid = uuid.v4();
    setLoading(true);

    const appointment = { name, cel, date, hour, tid, professional, service, uid, hours_index: hoursIndex };
    const result = await setDocument(appointment, tid);

    if (!result || !result.success) {
      setLoading(false);
      toast.error(result?.error || 'Não foi possível agendar. Escolha outro horário e tente novamente.');
      return;
    }

    setFormSubmitted(true);
    setLoading(false);
    setTransactionId(tid);
    toast.success('Agendamento confirmado! Redirecionando para o WhatsApp...');
    setTimeout(() => handleRedirect(hour[0], professional, service, tid), 3000);
  };

  useEffect(() => {
    localStorage.setItem('transactionId', transactionId);
  }, [transactionId]);

  useEffect(() => {
    if (!isAdmin && uid && documents) {
      documents.forEach((e) => { setName(e.name); setCel(e.cellphone); });
    }
  }, [uid, documents, isAdmin]);

  useEffect(() => {
    if (error) toast.error('Sem internet. Verifique sua conexão e tente novamente.');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (date && professional && enabledHours) {
      if (!navigator.onLine) { setAppointmentHours([]); setExclusiveHours([]); return; }
      const allHours = Array.isArray(hoursObject) ? hoursObject.map((h) => h.hour) : [];
      const terminalBlockedHours = getTerminalBlockedHours(allHours);
      if (!formSubmitted) setAppointmentHours(getOccupiedAppointmentHours(enabledHours, terminalBlockedHours));
    } else {
      setAppointmentHours([]);
    }
  }, [date, professional, enabledHours, formSubmitted, hoursObject]);

  useEffect(() => {
    const hours = Array.isArray(hoursObject) ? hoursObject.map((h) => h.hour) : [];
    if (hours.length > 0 && date && professional && service) {
      let filtered = getAvailableStartHours({ service, availableHours: hours, occupiedHours: appointmentHours });

      if (date === formatLocalDateInput()) {
        if (!navigator.onLine) { filtered = []; }
        else {
          const now = new Date().getTime();
          filtered = filtered.filter((h) => {
            const t = buildLocalDateTime(date, h)?.getTime();
            return t && now <= t - 600000;
          });
          if (!filtered.length) toast.info('Não há horários disponíveis para hoje com este barbeiro.');
        }
      } else if (!navigator.onLine) {
        filtered = [];
      } else if (!filtered.length) {
        toast.info('Não temos horários disponíveis com este barbeiro nesta data.');
      }

      setExclusiveHours(filtered);
    } else {
      setExclusiveHours([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentHours, hoursObject, date, professional, service]);

  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-text-muted">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col">

      {/* Hero */}
      <section className="bg-bg py-12 px-4 text-center">
        <div className="max-w-xl mx-auto flex flex-col items-center gap-4">
          <span className="inline-flex items-center gap-2 bg-primary-subtle text-primary text-xs font-semibold px-3 py-1 rounded-full">
            <CalendarCheck size={14} /> Agendamento online
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-text-primary leading-tight">
            Agende seu horário
          </h1>
          <p className="text-text-muted text-base max-w-sm">
            Escolha o serviço, barbeiro e horário ideal. Confirmação direto no WhatsApp.
          </p>
        </div>
      </section>

      {/* Card de agendamento */}
      <section className="flex-1 flex items-start justify-center px-4 pb-16 -mt-4">
        <Card className="w-full max-w-[460px]">
          <Card.Body>
            <ScheduleForm
              name={name} cel={cel} date={date} hour={hour}
              service={service} professional={professional}
              setName={setName} setCel={setCel}
              handleSubmit={handleSubmit}
              loading={loading} formSubmitted={formSubmitted}
              user={authUser} isAdmin={isAdmin}
              noDayOffBarber={noDayOffBarber}
              exclusiveHours={exclusiveHours}
              handleDateChange={handleDateChange}
              handleHourChange={handleHourChange}
              handleCelChange={handleCelChange}
              handleProfessionalChange={handleProfessionalChange}
              handelServiceChange={handelServiceChange}
            />
          </Card.Body>
        </Card>
      </section>
    </div>
  );
}
